import { Component } from '@theme/component';

/**
 * KWC countdown timer.
 *
 * A single live countdown that ticks down to an ISO 8601 target date. All
 * instances on the page share ONE ticker (per KWC spec: one setInterval driving
 * every countdown) so adding more timers costs nothing. The target date is
 * supplied by the global `sale_end_datetime` theme setting via the `end`
 * attribute, so changing it once updates every countdown site-wide.
 *
 * On expiry the component sets the `expired` attribute — CSS then swaps the
 * digits for the merchant's "expired" label. Purely presentational; not gated by
 * reduced-motion because a ticking value is information, not motion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 *
 * @extends {Component<Refs>}
 */
export class KwcCountdown extends Component {
  /** @type {Set<KwcCountdown>} */
  static #instances = new Set();
  /** @type {number|undefined} */
  static #ticker;

  /** @type {number} target time in ms since epoch */
  #target = NaN;

  connectedCallback() {
    super.connectedCallback();

    const end = this.getAttribute('end');
    this.#target = end ? Date.parse(end) : NaN;

    // No valid date → remove the element entirely so nothing empty renders.
    if (Number.isNaN(this.#target)) {
      this.remove();
      return;
    }

    this.#render();
    KwcCountdown.#register(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    KwcCountdown.#unregister(this);
  }

  /** @param {KwcCountdown} instance */
  static #register(instance) {
    KwcCountdown.#instances.add(instance);
    if (KwcCountdown.#ticker == null) {
      KwcCountdown.#ticker = window.setInterval(KwcCountdown.#tick, 1000);
    }
  }

  /** @param {KwcCountdown} instance */
  static #unregister(instance) {
    KwcCountdown.#instances.delete(instance);
    if (KwcCountdown.#instances.size === 0 && KwcCountdown.#ticker != null) {
      window.clearInterval(KwcCountdown.#ticker);
      KwcCountdown.#ticker = undefined;
    }
  }

  static #tick = () => {
    for (const instance of KwcCountdown.#instances) instance.#render();
  };

  #render() {
    const remaining = this.#target - Date.now();

    if (remaining <= 0) {
      this.#set(this.refs.days, 0);
      this.#set(this.refs.hours, 0);
      this.#set(this.refs.minutes, 0);
      this.#set(this.refs.seconds, 0);
      if (!this.hasAttribute('expired')) {
        this.setAttribute('expired', '');
        this.dispatchEvent(new CustomEvent('kwc:countdown:expired', { bubbles: true }));
      }
      KwcCountdown.#unregister(this);
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    this.#set(this.refs.days, Math.floor(totalSeconds / 86400));
    this.#set(this.refs.hours, Math.floor((totalSeconds % 86400) / 3600));
    this.#set(this.refs.minutes, Math.floor((totalSeconds % 3600) / 60));
    this.#set(this.refs.seconds, totalSeconds % 60);
  }

  /**
   * @param {HTMLElement|undefined} el
   * @param {number} value
   */
  #set(el, value) {
    if (!el) return;
    const next = value < 10 ? `0${value}` : `${value}`;
    if (el.textContent !== next) el.textContent = next;
  }
}

if (!customElements.get('kwc-countdown')) {
  customElements.define('kwc-countdown', KwcCountdown);
}
